type ValidationResult = {
  isValid: boolean;
  isDisabled: boolean;
  reason?: string;
};

export const validateCategoryToggle = (
  category: MappingCategory,
  allCategories: MappingCategory[]
): ValidationResult => {
  // Find all categories with the same reporting_category (excluding current one)
  const copiesWithSameReportingCategory = allCategories.filter(
    item => item.reporting_category === category.reporting_category && item.id !== category.id
  );

  // Check if this is a copy (has other categories with same reporting_category)
  const isCopy = copiesWithSameReportingCategory.length > 0;

  if (!isCopy) {
    // Not a copy, but still validate attribute pairs
    const hasAttributePairs = category.attribute && category.attribute.length > 0;

    if (hasAttributePairs) {
      // Check if any attribute pair has attribute selected but no attribute value
      const hasIncompleteAttributePair = category.attribute.some(pair => {
        const hasAttribute = Boolean(pair.attribute && pair.attribute.trim() !== '');
        const hasAttributeValue = Boolean(pair.attributeValue && pair.attributeValue.trim() !== '');
        // If attribute is selected but value is not, it's incomplete
        return hasAttribute && !hasAttributeValue;
      });

      if (hasIncompleteAttributePair) {
        return {
          isValid: false,
          isDisabled: true,
          reason: 'Attribute, attribute value and mapped category must be filled',
        };
      }
    }

    // No additional validation needed beyond mapped_categoryid
    return {
      isValid: true,
      isDisabled: false,
    };
  }

  // For copies, check if all required fields in attribute pairs are filled
  const hasAttributePairs = category.attribute && category.attribute.length > 0;

  if (!hasAttributePairs) {
    return {
      isValid: false,
      isDisabled: true,
      reason: 'Attribute, attribute value and mapped category must be filled',
    };
  }

  // Check if all attribute pairs have all fields filled
  const allPairsFilled = category.attribute.every(pair => {
    const hasAttribute = Boolean(pair.attribute && pair.attribute.trim() !== '');
    const hasAttributeValue = Boolean(pair.attributeValue && pair.attributeValue.trim() !== '');
    return hasAttribute && hasAttributeValue;
  });

  // Also check if mapped category is selected
  const hasMappedCategory = Boolean(category.mapped_categoryid && category.mapped_categoryid.trim() !== '');

  if (!allPairsFilled || !hasMappedCategory) {
    return {
      isValid: false,
      isDisabled: true,
      reason: 'Attribute, attribute value and mapped category must be filled',
    };
  }

  return {
    isValid: true,
    isDisabled: false,
  };
};

// Helper function to check if category has meaningful changes
export const hasMeaningfulChanges = (category: MappingCategory, mappingState: MappingState): boolean => {
  // Find the original category
  const originalCategory = mappingState.original.categories.find(cat => cat.id === category.id);

  // If category doesn't exist in original (newly created/copied), include it
  if (!originalCategory) {
    return true;
  }

  // Check if any non-attribute fields changed
  const fieldsToCheck: (keyof MappingCategory)[] = [
    'mapped_categoryid',
    'mappedcategory_name',
    'mapped_categorytype_id',
    'mapped_categorytype_name',
    'isConsidered',
    'isReviewed'
  ];

  for (const field of fieldsToCheck) {
    if (category[field] !== originalCategory[field]) {
      return true;
    }
  }

  // Check if attributes changed meaningfully
  const originalAttrs = originalCategory.attribute || [];
  const currentAttrs = category.attribute || [];

  // Filter out empty attribute pairs from both
  const filterEmptyPairs = (pairs: AttributeValuePair[]) =>
    pairs.filter(pair =>
      (pair.attribute && pair.attribute.trim() !== '') ||
      (pair.attributeValue && pair.attributeValue.trim() !== '')
    );

  const originalNonEmpty = filterEmptyPairs(originalAttrs);
  const currentNonEmpty = filterEmptyPairs(currentAttrs);

  // If counts are different, there's a meaningful change
  if (originalNonEmpty.length !== currentNonEmpty.length) {
    return true;
  }

  // Check if any attribute pair changed
  for (let i = 0; i < currentNonEmpty.length; i++) {
    const curr = currentNonEmpty[i];
    const orig = originalNonEmpty[i];
    if (curr.attribute !== orig.attribute || curr.attributeValue !== orig.attributeValue) {
      return true;
    }
  }

  return false;
};